import { useCallback } from 'react';
import { useLocale } from '@hooks/useLocale';
import type { LoginError as LoginErrorType } from '../../types/login';
import { LoginError, HookError } from '../../errorHandlers';
import { useCart } from '@adapters/bigcommerce/hooks/cart';
import { useCartUI } from '@context/CartContext';
import { useCustomer } from '@adapters/bigcommerce/hooks/customer/index';

interface LoginResponse {
  data: null;
  errors: LoginErrorType[];
}

export interface LoginCredentials {
  email: string;
  password: string;
  recaptchaToken?: string | null;
}

export const loginFetcher = async (
  { email, password, recaptchaToken }: LoginCredentials,
  locale: string
): Promise<LoginResponse> => {
  if (!(email && password)) {
    throw new LoginError([
      {
        message: 'Email and password must be provided to login',
        code: 'invalid.credentials',
      },
    ]);
  }

  const loginResponse = await fetch('/api/customer/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': locale,
    },
    body: JSON.stringify({ email, password, recaptchaToken }),
  });

  const { errors, data } = await loginResponse.json();

  if (loginResponse.status !== 200) {
    throw new LoginError(errors);
  }

  return data;
};

const useLogin = () => {
  const locale = useLocale();
  const cartHook = useCart();
  const { updateCart } = useCartUI();
  const { userLoggedIn } = useCustomer();

  if (!locale) throw new HookError('Locale must be specified to login');

  return useCallback(
    async (credentials: LoginCredentials) => {
      const result = await cartHook?.validateCart();
      updateCart(result);
      const data = await loginFetcher(credentials, locale);
      userLoggedIn();
      return data;
    },
    [cartHook, locale, updateCart, userLoggedIn]
  );
};

export default useLogin;
